"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  AddSubtractSteps,
  DivideSteps,
  EvaluateSteps,
  MultiplySteps,
  type PolyContext,
  RootsView,
} from "@/components/tools/algebra/polynomial-parts";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { findRoots, longDivision } from "@/lib/tool-kit/algebra/polynomial";
import {
  parseSides,
  type StatementError,
  singleVariable,
} from "@/lib/tool-kit/algebra/statement";
import {
  formatUni,
  type Uni,
  uniAdd,
  uniDegree,
  uniEval,
  uniLeading,
  uniMul,
  uniSub,
} from "@/lib/tool-kit/algebra/uni";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import {
  formatStyled,
  type NumberStyle,
  parseRational,
  type Rational,
} from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const POLYNOMIAL_CALCULATOR_STRINGS = {
  first: "Polynomial P",
  second: "Polynomial Q",
  polynomialHint:
    "Write it as you would on paper, for example 2x^3 − 3x^2 + 4x − 5 or (x + 2)(x − 1). Powers use ^ or superscripts.",
  operation: "Operation",
  operations: {
    add: "Add: P + Q",
    subtract: "Subtract: P − Q",
    multiply: "Multiply: P × Q",
    divide: "Divide: P ÷ Q (long division)",
    evaluate: "Evaluate: P at a value",
    roots: "Roots of P",
  },
  at: "Value to substitute",
  atHint: "A number, decimal or fraction such as 3/2.",
  results: "Result",
  result: "Result",
  quotient: "Quotient",
  remainder: "Remainder",
  value: "Value",
  properties: "Properties of the result",
  degree: "Degree",
  degreeNames: [
    "Zero polynomial",
    "Constant",
    "Linear",
    "Quadratic",
    "Cubic",
    "Quartic",
    "Quintic",
  ],
  degreeOther: "Degree {n}",
  leading: "Leading coefficient",
  constantTerm: "Constant term",
  terms: "one: Term|other: Terms",
  rootsHeading: "Roots of the result",
  noRoots: "There are no roots: the polynomial is a non-zero constant.",
  multiplicity: "multiplicity {n}",
  numericNote:
    "Roots marked ≈ are numerical approximations (four decimal places), found after the exact rational roots were divided out. Treat them as a check, not a proof.",
  largeNote:
    "The coefficients are too large to search for rational roots exhaustively, so the roots below are all numerical.",
  allNumbers: "Every number is a root of the zero polynomial.",
  copy: "Copy result",
  copied: "Copied!",
  stepsHeading: "Working",
  writeSum: "Write the sum.",
  writeDifference: "Write the difference.",
  distributeMinus:
    "Distribute the minus sign: it changes the sign of every term of Q.",
  combineLike: "Combine like terms — the ones with the same power.",
  power: "Power",
  firstPolynomial: "P",
  secondPolynomial: "Q",
  negatedSecond: "−Q",
  sum: "Result",
  multiplyIntro: "Multiply every term of P by Q, then add the results.",
  partialProduct: "One term of P times Q:",
  addPartials: "Add the partial products and combine like terms:",
  divideIntro: "Divide P by Q with long division.",
  divideRound:
    "Round {n}: divide the leading terms, {dividend} ÷ {divisor} = {term}, multiply it by Q, and subtract.",
  remainderSoFar: "Left over:",
  divideExact:
    "The remainder is 0, so Q divides P exactly and is a factor of P.",
  divideRemainder:
    "The degree of what is left is lower than the degree of Q, so the division stops.",
  divideCheck: "Check: {divisor} × {quotient} + {remainder} = {total}",
  syntheticIntro:
    "Synthetic division (Horner's scheme) by ({variable} − {at}): bring down the first coefficient, multiply by {at}, add to the next coefficient, and repeat. The last total is the remainder.",
  syntheticRow: "Degree",
  coefficients: "Coefficients",
  carried: "Multiply by the value",
  totals: "Add down",
  substitute: "Substitute {variable} = {at} and work out the arithmetic.",
  remainderTheorem:
    "By the remainder theorem, this value is also the remainder when P is divided by ({variable} − {at}).",
  enter: "Enter a polynomial to begin.",
  needSecond: "Enter the second polynomial Q.",
  divideByZero: "Q is zero, and you cannot divide by zero.",
  badValue: "Enter the value as a number or a fraction, such as 2 or 3/2.",
  errors: {
    empty: "Enter a polynomial to begin.",
    "too-long": "That expression is too long. Shorten it and try again.",
    character:
      "The character “{token}” is not allowed. Use digits, one letter, + − × / ^ and brackets.",
    unexpected: "Something is out of place near “{token}”.",
    end: "The expression ends too soon. Check for a trailing operator.",
    bracket: "The brackets do not match.",
    "divide-by-variable":
      "Dividing by the variable does not give a polynomial. Divide only by numbers.",
    "divide-by-zero": "Division by zero is not allowed.",
    exponent: "Powers must be whole numbers, such as x^2.",
    "too-large": "That expression is too large to expand (degree above 40).",
    "no-relation": "Enter an expression, not an equation.",
    "many-relations": "Enter an expression, not an equation.",
    "many-variables":
      "More than one letter appears ({token}). Polynomials here have one variable.",
    "missing-side": "Enter a polynomial.",
  },
} as const;

type Strings = typeof POLYNOMIAL_CALCULATOR_STRINGS;
type Operation = keyof Strings["operations"];

const DECIMAL_INPUT = /[.,]/;
const OPERATIONS: Operation[] = [
  "add",
  "subtract",
  "multiply",
  "divide",
  "evaluate",
  "roots",
];

type Reading =
  | { ok: false; message: string }
  | { ok: true; variable: string; p: Uni; q: Uni };

function errorMessage(error: StatementError, strings: Strings): string {
  const template = strings.errors[error.code as keyof Strings["errors"]];
  return t(template ?? strings.errors.empty, { token: error.token });
}

function read(
  pText: string,
  qText: string,
  needsQ: boolean,
  strings: Strings
): Reading {
  const texts = needsQ ? [pText, qText] : [pText];
  const parsed = parseSides(texts);
  if (!parsed.ok) {
    return { ok: false, message: errorMessage(parsed.error, strings) };
  }
  const variable = singleVariable(parsed.polys);
  if (!variable.ok) {
    return { ok: false, message: errorMessage(variable.error, strings) };
  }
  return {
    ok: true,
    variable: variable.variable,
    p: variable.parts[0],
    q: variable.parts[1] ?? [],
  };
}

function degreeName(p: Uni, strings: Strings): string {
  const degree = uniDegree(p);
  const named = strings.degreeNames[degree + 1];
  return named ?? t(strings.degreeOther, { n: degree });
}

function Properties({ result, ctx }: { result: Uni; ctx: PolyContext }) {
  const { strings } = ctx;
  const locale = useToolLocale();
  const show = (value: Rational) => formatStyled(value, ctx.style, ctx.mark);
  const count = result.filter((c) => c.num !== BigInt(0)).length;
  return (
    <StatGrid>
      <StatCard label={strings.degree} value={degreeName(result, strings)} />
      <StatCard
        label={strings.leading}
        value={result.length === 0 ? "0" : show(uniLeading(result))}
      />
      <StatCard
        label={strings.constantTerm}
        value={result.length === 0 ? "0" : show(result[0])}
      />
      <StatCard
        label={tPlural(strings.terms, locale, count).trim()}
        value={count}
      />
    </StatGrid>
  );
}

function Outcome({
  op,
  reading,
  atText,
  strings,
  style,
}: {
  op: Operation;
  reading: Extract<Reading, { ok: true }>;
  atText: string;
  strings: Strings;
  style: NumberStyle;
}) {
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const { p, q, variable } = reading;
  const ctx: PolyContext = { variable, strings, style, mark };
  const show = (value: Rational) => formatStyled(value, style, mark);
  const out = (poly: Uni) => formatUni(poly, variable, style, mark);

  if (op === "evaluate") {
    const at = parseRational(atText);
    if (!at) {
      return <ErrorNotice>{strings.badValue}</ErrorNotice>;
    }
    return (
      <>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.value}
            value={show(uniEval(p, at))}
          />
        </StatGrid>
        <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
        <EvaluateSteps at={at} ctx={ctx} p={p} />
      </>
    );
  }
  if (op === "roots") {
    return <RootsOnly ctx={ctx} p={p} />;
  }
  if (op === "divide") {
    if (q.length === 0) {
      return <ErrorNotice>{strings.divideByZero}</ErrorNotice>;
    }
    const { quotient, remainder } = longDivision(p, q);
    return (
      <>
        <StatGrid>
          <StatCard emphasis label={strings.quotient} value={out(quotient)} />
          <StatCard label={strings.remainder} value={out(remainder)} />
        </StatGrid>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copy}
          small
          text={`${out(quotient)}; ${strings.remainder}: ${out(remainder)}`}
        />
        <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
        <DivideSteps ctx={ctx} p={p} q={q} />
      </>
    );
  }
  const result = combined(op, p, q);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.result} value={out(result)} />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={out(result)}
      />
      <h3 className={WIDGET_H3}>{strings.properties}</h3>
      <Properties ctx={ctx} result={result} />
      <h3 className={WIDGET_H3}>{strings.stepsHeading}</h3>
      {op === "multiply" ? (
        <MultiplySteps ctx={ctx} p={p} q={q} />
      ) : (
        <AddSubtractSteps ctx={ctx} p={p} q={q} subtract={op === "subtract"} />
      )}
      <h3 className={WIDGET_H3}>{strings.rootsHeading}</h3>
      <ResultRoots ctx={ctx} result={result} />
    </>
  );
}

function combined(op: Operation, p: Uni, q: Uni): Uni {
  if (op === "add") {
    return uniAdd(p, q);
  }
  return op === "subtract" ? uniSub(p, q) : uniMul(p, q);
}

function ResultRoots({ result, ctx }: { result: Uni; ctx: PolyContext }) {
  if (result.length === 0) {
    return <Notice>{ctx.strings.allNumbers}</Notice>;
  }
  return <RootsView ctx={ctx} roots={findRoots(result)} />;
}

function RootsOnly({ p, ctx }: { p: Uni; ctx: PolyContext }) {
  const { strings } = ctx;
  const out = (poly: Uni) => formatUni(poly, ctx.variable, ctx.style, ctx.mark);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.result} value={out(p)} />
      </StatGrid>
      <Properties ctx={ctx} result={p} />
      <h3 className={WIDGET_H3}>{strings.rootsHeading}</h3>
      <ResultRoots ctx={ctx} result={p} />
    </>
  );
}

export default function PolynomialCalculatorWidget() {
  const strings = useToolUi(POLYNOMIAL_CALCULATOR_STRINGS);
  const [pText, setP] = useState("2x^3 − 3x^2 + 4x − 5");
  const [qText, setQ] = useState("x − 2");
  const [op, setOp] = useState<Operation>("divide");
  const [atText, setAt] = useState("2");
  const needsQ = op !== "evaluate" && op !== "roots";
  const options = OPERATIONS.map((value) => ({
    value,
    label: strings.operations[value],
  }));
  const style: NumberStyle = DECIMAL_INPUT.test(
    `${pText} ${needsQ ? qText : ""}`
  )
    ? "decimal"
    : "fraction";
  let body = <Notice>{strings.enter}</Notice>;
  if (pText.trim() !== "" && (!needsQ || qText.trim() !== "")) {
    const reading = read(pText, qText, needsQ, strings);
    body = reading.ok ? (
      <Outcome
        atText={atText}
        op={op}
        reading={reading}
        strings={strings}
        style={style}
      />
    ) : (
      <ErrorNotice>{reading.message}</ErrorNotice>
    );
  } else if (needsQ && pText.trim() !== "") {
    body = <Notice>{strings.needSecond}</Notice>;
  }
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.polynomialHint}
          id="poly-p"
          label={strings.first}
          mono
          onChange={setP}
          value={pText}
        />
        {needsQ ? (
          <TextField
            id="poly-q"
            label={strings.second}
            mono
            onChange={setQ}
            value={qText}
          />
        ) : null}
        {op === "evaluate" ? (
          <TextField
            hint={strings.atHint}
            id="poly-at"
            label={strings.at}
            mono
            onChange={setAt}
            value={atText}
          />
        ) : null}
      </div>
      <SelectField
        id="poly-op"
        label={strings.operation}
        onChange={setOp}
        options={options}
        value={op}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
